/**
 * What the questionnaire collected, and what it does with it.
 *
 * ## Every field is nullable, and that is the design
 *
 * The flow saves after every screen, so a dropped connection loses nothing —
 * which means a half-answered questionnaire has to be a representable value
 * rather than an error state. `null` is "not answered yet", distinct from an
 * answer of none: `maxDailyLoss: null` inside a *completed* prop section means
 * the firm has no daily loss limit, which several genuinely do not.
 *
 * ## Nothing here is a house style
 *
 * CLAUDE.md forbids one, and a questionnaire is the easiest place in a product
 * to smuggle one in — a pre-ticked box is a decision made on the user's
 * behalf. So the answers carry no defaults of their own: an unanswered field
 * leaves the existing preference exactly as it was.
 *
 * ## It never overwrites a hand edit
 *
 * A user can open Settings mid-questionnaire, change their timezone, and come
 * back. Writing the questionnaire's answer over the top would silently undo
 * something they did deliberately and more recently. `applyAnswers` takes
 * `prefsEditedSinceStart` and, when it is set, keeps every preference the user
 * already has — reporting which, so the last screen can say so rather than
 * leaving them to discover it.
 */
import { dec, type Decimal } from '../money.js';
import type {
  DailyLossBasis, DrawdownType, RuleSet,
} from '../rules/types.js';
import { findFirm, firmNumbers } from './firms.js';

/** How a chart is drawn by default. Presentation only — it changes no figure. */
export type ChartStyle = 'candles' | 'hollow' | 'bars' | 'line';

export type AccountKind = 'evaluation' | 'funded' | 'own' | 'both';

/** Money and counts travel as strings, for the reason every other DTO does. */
export interface OnboardingAnswers {
  readonly instruments: readonly string[];
  readonly accountKind: AccountKind | null;

  readonly firm: string | null;
  readonly accountSize: string | null;
  readonly profitTarget: string | null;
  /** Null once answered means the firm has no daily loss limit. */
  readonly maxDailyLoss: string | null;
  readonly maxDrawdown: string | null;
  readonly drawdownType: DrawdownType | null;
  readonly drawdownFreezeAt: string | null;
  readonly minTradingDays: number | null;
  readonly consistency: number | null;

  readonly ownDailyLoss: string | null;
  readonly ownRiskPerTrade: string | null;
  readonly ownMaxContracts: number | null;
  readonly ownMaxTrades: number | null;

  readonly dailyLossBasis: DailyLossBasis | null;
  readonly timezone: string | null;
  readonly sessionScope: 'rth' | 'eth' | null;
  readonly defaultTimeframe: string | null;
  readonly chartStyle: ChartStyle | null;
}

export const EMPTY_ANSWERS: OnboardingAnswers = {
  instruments: [],
  accountKind: null,
  firm: null, accountSize: null, profitTarget: null, maxDailyLoss: null,
  maxDrawdown: null, drawdownType: null, drawdownFreezeAt: null,
  minTradingDays: null, consistency: null,
  ownDailyLoss: null, ownRiskPerTrade: null, ownMaxContracts: null, ownMaxTrades: null,
  dailyLossBasis: null, timezone: null, sessionScope: null,
  defaultTimeframe: null, chartStyle: null,
};

/** Which screens this trader sees. The prop branch is two screens longer. */
export type StepId =
  | 'welcome' | 'instruments' | 'account' | 'firm' | 'numbers'
  | 'limits' | 'when' | 'chart' | 'done';

const PROP_STEPS: readonly StepId[] = [
  'welcome', 'instruments', 'account', 'firm', 'numbers', 'when', 'chart', 'done',
];
const OWN_STEPS: readonly StepId[] = [
  'welcome', 'instruments', 'account', 'limits', 'when', 'chart', 'done',
];
const BOTH_STEPS: readonly StepId[] = [
  'welcome', 'instruments', 'account', 'firm', 'numbers', 'limits', 'when', 'chart', 'done',
];

/**
 * The flow for these answers.
 *
 * Derived rather than stored, so a trader who changes their mind on the
 * account question does not strand themselves on a screen that no longer
 * belongs to their path.
 *
 * Before the account question is answered the prop path is assumed, because it
 * is the longer one — a progress rail that grows as you go reads as the app
 * having misled you, while one that shrinks reads as a shortcut.
 */
export function stepsFor(kind: AccountKind | null): readonly StepId[] {
  if (kind === 'own') return OWN_STEPS;
  if (kind === 'both') return BOTH_STEPS;
  return PROP_STEPS;
}

/** Screens that ask a question, for "3 of 6". Welcome and done ask nothing. */
export function questionCount(kind: AccountKind | null): number {
  return stepsFor(kind).filter((s) => s !== 'welcome' && s !== 'done').length;
}

const money = (v: string | null): Decimal | undefined =>
  v === null || v.trim() === '' ? undefined : dec(v);

/**
 * Fill the numbers screen from a firm and a size.
 *
 * Separate from `applyAnswers` because it runs on every keystroke of the size
 * field while the user is still deciding, and it must not touch anything the
 * user has already typed over.
 */
export function prefillFromFirm(answers: OnboardingAnswers): OnboardingAnswers {
  const size = Number(answers.accountSize ?? 0);
  const n = firmNumbers(findFirm(answers.firm), size);
  return {
    ...answers,
    profitTarget: n.profitTarget?.toFixed(2) ?? null,
    maxDailyLoss: n.maxDailyLoss?.toFixed(2) ?? null,
    maxDrawdown: n.maxDrawdown?.toFixed(2) ?? null,
    drawdownType: n.drawdownType,
    drawdownFreezeAt: n.drawdownFreezeAt?.toFixed(2) ?? null,
    dailyLossBasis: n.dailyLossBasis,
    minTradingDays: n.minTradingDays,
    consistency: n.consistency,
  };
}

/** The preference fields the questionnaire is allowed to write. */
export interface PreferencePatch {
  readonly timezone?: string;
  readonly sessionScope?: 'rth' | 'eth';
  readonly dailyLossBasis?: DailyLossBasis;
  readonly defaultTimeframe?: string;
  readonly chartStyle?: ChartStyle;
}

export interface ApplyResult {
  /** Null means leave the watchlist exactly as it is. */
  readonly watchlist: readonly string[] | null;
  readonly preferences: PreferencePatch;
  /** Null when no rule numbers were given — a rule set of guesses is worse than none. */
  readonly ruleSet: RuleSet | null;
  /**
   * Preferences the questionnaire had an answer for and did NOT write, because
   * the user had already changed them by hand.
   */
  readonly kept: readonly string[];
}

export interface ApplyOptions {
  readonly accountId: string;
  /** True when the user edited preferences by hand after starting. */
  readonly prefsEditedSinceStart?: boolean;
  /** Falls back to the account size when the user gave no starting balance. */
  readonly startingBalance?: Decimal;
}

/**
 * Turn answers into the three things they change.
 *
 * Pure, and deliberately so: the same function decides what the phone writes
 * and what the desktop writes, which is the only way the two can be relied on
 * to agree.
 */
export function applyAnswers(a: OnboardingAnswers, opts: ApplyOptions): ApplyResult {
  const wanted: { -readonly [K in keyof PreferencePatch]: PreferencePatch[K] } = {};
  if (a.timezone !== null) wanted.timezone = a.timezone;
  if (a.sessionScope !== null) wanted.sessionScope = a.sessionScope;
  if (a.dailyLossBasis !== null) wanted.dailyLossBasis = a.dailyLossBasis;
  if (a.defaultTimeframe !== null) wanted.defaultTimeframe = a.defaultTimeframe;
  if (a.chartStyle !== null) wanted.chartStyle = a.chartStyle;

  const kept = opts.prefsEditedSinceStart ? Object.keys(wanted) : [];
  const preferences = opts.prefsEditedSinceStart ? {} : wanted;

  const prop = a.accountKind === 'evaluation' || a.accountKind === 'funded'
    || a.accountKind === 'both';
  const size = money(a.accountSize) ?? opts.startingBalance;

  let ruleSet: RuleSet | null = null;
  if (prop && size !== undefined) {
    const firm = findFirm(a.firm);
    ruleSet = {
      id: `onboarding:${opts.accountId}`,
      kind: 'prop_firm',
      firm: firm?.name,
      name: `${firm?.name ?? 'My rules'} · ${size.toFixed(0)}`,
      accountType: a.accountKind === 'evaluation' ? 'evaluation' : 'funded',
      startingBalance: size,
      profitTarget: money(a.profitTarget),
      maxDailyLoss: money(a.maxDailyLoss),
      // Only the drawdown answer decides this; a preference the user set on a
      // different screen must not silently re-answer a firm's rule.
      dailyLossBasis: a.dailyLossBasis ?? 'realized',
      maxDrawdown: money(a.maxDrawdown),
      drawdownType: a.drawdownType ?? 'static',
      drawdownFreezeAt: money(a.drawdownFreezeAt),
      minTradingDays: a.minTradingDays ?? undefined,
      consistency: a.consistency
        ? { maxSingleDayShareOfTotal: a.consistency }
        : undefined,
    };
  } else if (a.accountKind === 'own' && size !== undefined) {
    ruleSet = {
      id: `onboarding:${opts.accountId}`,
      kind: 'self_directed',
      name: 'My limits',
      accountType: 'live',
      startingBalance: size,
      maxDailyLoss: money(a.ownDailyLoss),
      dailyLossBasis: a.dailyLossBasis ?? 'realized',
      drawdownType: 'static',
      maxRiskPerTrade: money(a.ownRiskPerTrade),
      maxOpenContracts: a.ownMaxContracts ?? undefined,
      maxDailyTrades: a.ownMaxTrades ?? undefined,
    };
  }

  return {
    // An empty selection is an answer — leave the watchlist alone rather than
    // clearing one the user may already have built.
    watchlist: a.instruments.length > 0 ? a.instruments : null,
    preferences,
    ruleSet,
    kept,
  };
}

/** Whether a given screen has been answered. Welcome and done ask nothing. */
export function isAnswered(step: StepId, a: OnboardingAnswers): boolean {
  switch (step) {
    case 'instruments': return a.instruments.length > 0;
    case 'account': return a.accountKind !== null;
    case 'firm': return a.firm !== null;
    case 'numbers': return a.maxDrawdown !== null || a.maxDailyLoss !== null;
    case 'limits':
      return a.ownDailyLoss !== null || a.ownRiskPerTrade !== null
        || a.ownMaxContracts !== null || a.ownMaxTrades !== null;
    case 'when': return a.timezone !== null || a.sessionScope !== null;
    case 'chart': return a.defaultTimeframe !== null || a.chartStyle !== null;
    default: return true;
  }
}

/**
 * Where a resumed questionnaire should open.
 *
 * "Pick up where you left off" has to mean it. Reopening on the welcome screen
 * with the answers silently intact makes the button a small lie, and asks the
 * user to click through screens they have already done.
 *
 * A user who has answered nothing gets the welcome screen, because for them
 * there is nowhere to pick up from — it is a first run, not a resume.
 *
 * A user who answered everything and skipped anyway lands on the LAST
 * question, one tap from finishing, rather than on a recap for a
 * questionnaire they never completed.
 */
export function resumeStep(a: OnboardingAnswers): number {
  const steps = stepsFor(a.accountKind);
  const questions = steps.filter((s) => s !== 'welcome' && s !== 'done');

  if (!questions.some((s) => isAnswered(s, a))) return 0;

  const next = steps.findIndex((s) => s !== 'welcome' && s !== 'done' && !isAnswered(s, a));
  return next === -1 ? steps.length - 2 : next;
}
